import Cookies from "js-cookie";

import { Cart, CustomCartItemData, HeldCartProduct } from "../types/cart";
import { CustomSuggestionOption } from "../types/posProduct";
import { Product } from "../types/product";
import { get, post, put } from "./fetch.core";

export async function clearCart() {
  await post("/pos/cart/removeAllProducts", {
    cartId: await getPosCartId(),
  });
}

export async function addToPosCart({
  product,
  quantity = 1,
  warehouseId,
}: {
  product: Product;
  quantity: number;
  warehouseId: string;
}) {
  const res = await post<{ cartItemId: string }>("/pos/cart/addProduct", {
    cartId: await getPosCartId(),
    productId: product.id,
    quantity: quantity,
    warehouseId,
  });

  return res.data;
}

export async function addCustomToPosCart(data: CustomCartItemData) {
  const obj = { ...data, productId: data.product.id };
  //  @ts-ignore
  delete obj.product;

  await post("/pos/cart/addCustomProduct", {
    cartId: await getPosCartId(),
    ...obj,
  });
}

export async function removeFromCart({ cartItemId }: { cartItemId: string }) {
  await post("/pos/cart/removeItem", {
    cartId: await getPosCartId(),
    cartItemId,
  });
}

export async function updateCartItem({ cartItemId, quantity }: { cartItemId: string; quantity: number }) {
  await post("/pos/cart/updateItem", {
    cartId: await getPosCartId(),
    cartItemId: cartItemId,
    quantity,
  });
}

export async function getPosCartId() {
  let cartId = Cookies.get("posCartId");

  if (cartId === undefined) {
    const { data } = await get<{ cartId: string }>("/pos/cart/getId");
    cartId = data.cartId;

    if (cartId !== undefined) {
      Cookies.set("posCartId", cartId, { sameSite: "none", secure: true });
    }
  }

  return cartId;
}

export async function getPosCart(locale: any): Promise<Cart> {
  const cartId = await getPosCartId();

  if (!cartId) {
    //  @ts-ignore
    return null;
  }

  const res = await get<Cart>(`/pos/cart/get?cartId=${cartId}`, {
    locale,
    useRetailShopId: true,
    useShopId: true,
    useToken: true,
  });

  return res.data;
}

export async function getCartTotal() {
  const fetchedCart = await getPosCart({ locale: "en" });

  const cartTotal = fetchedCart.items
    .map((item) => {
      const cartPrice = item.discountPrice ? item.discountPrice : item.itemPrice;
      return item.quantity * cartPrice;
    })
    .reduce((r, i) => i + r, 0);

  return cartTotal;
}

export async function getPosCartCount(): Promise<any> {
  const cartId = await getPosCartId();
  const res: any = await get(`/pos/cart/getCount?cartId=${cartId}`);

  return res.data.count;
}

export async function setCartStatusToHold({ customerId }: { customerId?: string }): Promise<any> {
  const res: any = await post("/pos/cart/updateStatus", {
    cartId: await getPosCartId(),
    customerId,
    status: "onHold",
  });

  Cookies.remove("posCartId");
  Cookies.set("posCartId", res.data.cartId, { sameSite: "strict" });
}

export async function activeHeldCart({ cartId, customerId }: { customerId: string; cartId: string }): Promise<any> {
  const res: any = await post("/pos/cart/updateStatus", {
    cartId,
    customerId,
    status: "active",
  });

  Cookies.remove("posCartId");
  Cookies.set("posCartId", res.data.cartId, { sameSite: "strict" });
}

export async function listOnHoldCarts() {
  const res = await get<HeldCartProduct[]>("/pos/cart/listOnHold");
  return res.data;
}

export async function deleteHeldCart(cartId: string) {
  await post("/pos/cart/delete", { cartId });
}

export async function overrideProductPrice({ productId, price }: { productId: string; price: number }) {
  await post("/pos/cart/overridePrice", {
    cartId: await getPosCartId(),
    price,
    productId,
  });
}

export async function removePriceOverride(productId: string) {
  await put("/pos/cart/removeOverridePrice", {
    cartId: await getPosCartId(),
    productId,
  });
}

export async function addNoteToCartProduct({ productId, note }: { productId: string; note: string }) {
  await post("/pos/cart/addNote", {
    cartId: await getPosCartId(),
    note,
    productId,
  });
}
